import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { usePathParam, useViewport } from '@chilecompra/react-kit'
import { Skeleton, Table, TableRow, Toast, Typography } from '@chilecompra/react-kit/components'
import { onGetCompanyManagersThunk, onGetCompanyManagersSiiThunk } from './CompanyAdministrationSection.actions'
import TablePlaceholder from '../../components/Placeholders/TablePlaceholder'
import { Content, Separator, TableCell } from './CompanyAdministrationSection.styles'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { USER_TYPE } from '../../config/settings/constant'
import useReCaptcha from '../../modules/hooks/reCaptcha'
/**
 * The Company Administration section container.
 */
const CompanyAdministrationSection = () => {
  const { userType } = useAuthorization()
  const { executeReCaptcha } = useReCaptcha()
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const {
    size: { isMobile }
  } = useViewport()
  const { sellerType, orgLegalName } = useSelector(state => state.basicSellerInformation)
  const { error, loading, managers, source } = useSelector(state => state.companyAdministration)
  const { error: summaryError } = useSelector(state => state.summarySection)

  useEffect(() => {
    if (!loading || sellerType === '') return
    if (userType === USER_TYPE.PUBLIC) {
      const executeAll = async () => {
        await executeReCaptcha({ action: 'publicInfo' })
        const payload = { dni, userType }
        dispatch(
          onGetCompanyManagersThunk({
            payload,
            callback: executeCompanyManagersSii
          })
        )
      }

      executeAll()
    } else {
      const payload = { dni, userType }
      dispatch(
        onGetCompanyManagersThunk({
          payload,
          callback: executeCompanyManagersSii
        })
      )
    }
  }, [sellerType])

  const executeCompanyManagersSii = () => {
    if (userType === USER_TYPE.PUBLIC) {
      const executeAll = async () => {
        await executeReCaptcha({ action: 'publicInfo' })
        dispatch(onGetCompanyManagersSiiThunk({ dni }))
      }
      executeAll()
    } else {
      dispatch(onGetCompanyManagersSiiThunk({ dni }))
    }
  }

  return error?.reason === 'FORBIDDEN_ERROR' || summaryError?.reason === 'FORBIDDEN_ERROR' ? null : (
    <>
      <Content padding="24px 0 10px">
        <Typography fontWeight="bold" variant="h4">
          Administración de la empresa
        </Typography>

        {loading && <Skeleton width="50%" />}
        {loading && <TablePlaceholder margin="9px 0 0" />}

        {!loading && source === 'sii' && (
          <Typography padding="0 0 16px" variant="subtitle">
            Fuente: Servicio de Impuestos Internos.
          </Typography>
        )}
        {!loading && source === 'affidavit' && (
          <Typography padding="0 0 16px" variant="subtitle">
            Fuente: Declaración Jurada del Registro de Proveedores ingresada por el proveedor en Mercado Público.
          </Typography>
        )}

        {!error && !loading && managers?.length !== 0 && sellerType !== 'natural' && (
          <Table accessibility={{ label: 'Administración de la empresa' }}>
            {managers?.map((manager, index) => {
              return (
                <TableRow key={`manager-${index}`}>
                  <TableCell
                    display={userType !== USER_TYPE.PUBLIC && manager.dni ? 'block' : 'none'}
                    disableBottomBorder
                    padding="0 9px 0 0"
                    width={isMobile ? '100px' : '150px'}
                  >
                    <Typography variant="body2">{manager.dni}</Typography>
                  </TableCell>
                  <TableCell disableBottomBorder padding="0 9px 0 0" width={isMobile ? '100px' : '150px'}>
                    <Typography variant="body2">{manager.position}</Typography>
                  </TableCell>
                  <TableCell disableBottomBorder padding="0">
                    <Typography variant="body2">{manager.name}</Typography>
                  </TableCell>
                </TableRow>
              )
            })}
          </Table>
        )}

        {!error && !loading && sellerType === 'natural' && (
          <Table accessibility={{ label: 'Administración de la empresa' }}>
            <TableRow>
              <TableCell disableBottomBorder padding="0 9px 0 0" width="150px">
                {userType !== USER_TYPE.PUBLIC && <Typography variant="body2">{dni}</Typography>}
              </TableCell>
              <TableCell disableBottomBorder padding="0 9px 0 0" width="150px">
                <Typography variant="body2">Representante Legal</Typography>
              </TableCell>
              <TableCell disableBottomBorder padding="0">
                <Typography variant="body2">{orgLegalName}</Typography>
              </TableCell>
            </TableRow>
          </Table>
        )}

        {!loading && error && error?.reason === 'NOT_FOUND_ERROR' && (
          <Typography color="black3" variant="body2">
            Este proveedor no ha ingresado información.
          </Typography>
        )}

        {!loading && error && error?.reason !== 'NOT_FOUND_ERROR' && (
          <Toast
            disableBackground
            margin="-9px 0 -9px -17px"
            textDirection="vertical"
            title="Lo sentimos, estamos presentando problemas con nuestro servidor"
            severity="default"
          >
            En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
            más.
          </Toast>
        )}
      </Content>
      <Separator />
    </>
  )
}

export default CompanyAdministrationSection
